import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  HeartHandshake, CalendarClock, Stethoscope, Gift, History, Clock, CheckCircle2, ExternalLink, DollarSign, Award,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (s) => {
  if (!s) return '—';
  try {
    const d = new Date(`${String(s).slice(0, 10)}T00:00:00`);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return s; }
};
const fmtDateTime = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return s; }
};
const h = (n) => `${Number(n || 0).toFixed(2)}h`;

const STATUS_STYLE = {
  pending: 'bg-yellow-500/10 text-yellow-400',
  approved: 'bg-green-500/10 text-green-400',
  rejected: 'bg-red-500/10 text-red-400',
  canceled: 'bg-[#333] text-[#A3A3A3]',
};

const Stat = ({ label, value, accent }) => (
  <div className="min-w-0">
    <p className="text-[11px] uppercase tracking-wider text-[#A3A3A3] truncate">{label}</p>
    <p className={`text-xl sm:text-2xl font-bold ${accent || 'text-white'}`}>{value}</p>
  </div>
);

const Card = ({ title, icon: Icon, children, testid }) => (
  <div data-testid={testid} className="bg-[#141414] border border-[#262626] rounded-xl p-4 sm:p-5">
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon className="w-4 h-4 text-[#D4AF37]" />}
      <h2 className="text-white font-semibold text-sm uppercase tracking-wider">{title}</h2>
    </div>
    {children}
  </div>
);

const LeaveRow = ({ item }) => (
  <div data-testid={`leave-row-${item.id}`} className="rounded-lg border border-[#262626] bg-[#0F0F0F] p-3">
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-white font-medium text-sm truncate">{item.leave_label}</span>
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_STYLE[item.status] || 'bg-[#333] text-[#A3A3A3]'}`}>{item.status}</span>
      </div>
      <span className="text-sm text-white">{h(item.hours)} {item.requested ? <span className="text-yellow-400 text-xs">requested</span> : item.paid ? <span className="text-green-400 text-xs">paid</span> : <span className="text-[#A3A3A3] text-xs">unpaid</span>}</span>
    </div>
    <div className="mt-1.5 text-xs text-[#A3A3A3] flex flex-wrap gap-x-4 gap-y-1">
      <span>{fmtDate(item.start_date)}{item.end_date && item.end_date !== item.start_date ? ` → ${fmtDate(item.end_date)}` : ''}{item.partial_day && item.partial_start ? ` · ${item.partial_start}–${item.partial_end}` : ''}</span>
      {item.approval_date && <span>Approved {fmtDateTime(item.approval_date)}</span>}
    </div>
    {(item.pto_deducted > 0 || item.pto_restored > 0) && (
      <div className="mt-1.5 text-xs flex flex-wrap gap-x-4">
        {item.pto_deducted > 0 && <span className="text-red-400">−{h(item.pto_deducted)} PTO used</span>}
        {item.pto_restored > 0 && <span className="text-green-400">+{h(item.pto_restored)} PTO restored</span>}
        {item.remaining_after != null && <span className="text-[#A3A3A3]">Remaining {h(item.remaining_after)}</span>}
      </div>
    )}
  </div>
);

const Section = ({ title, items, empty, testid }) => (
  <div data-testid={testid}>
    <p className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider mb-2">{title} <span className="text-[#525252]">({items.length})</span></p>
    {items.length === 0 ? (
      <p className="text-xs text-[#525252] mb-4">{empty}</p>
    ) : (
      <div className="space-y-2 mb-4">{items.map((it) => <LeaveRow key={it.id} item={it} />)}</div>
    )}
  </div>
);

const MyBenefits = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comp, setComp] = useState(null);      // iter257 — self-only compensation
  const [bonuses, setBonuses] = useState([]);  // iter257 — self bonus history

  useEffect(() => {
    axios.get(`${API}/pto/my-benefits`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    axios.get(`${API}/me/pay-rate-history`, { withCredentials: true })
      .then(({ data }) => setComp(data)).catch(() => setComp(null));
    axios.get(`${API}/bonus-requests/me`, { withCredentials: true })
      .then(({ data }) => setBonuses(Array.isArray(data) ? data : (data.items || data.requests || [])))
      .catch(() => setBonuses([]));
  }, []);

  if (loading) return <div className="p-6 text-[#A3A3A3]">Loading your benefits…</div>;
  if (!data) return <div className="p-6 text-[#A3A3A3]">Unable to load benefits.</div>;

  const { pto, sick, paid_holiday_eligible, history, ledger } = data;
  const holidays = data.holidays || { upcoming: [], past: [] };
  const benefitLinks = data.benefit_links || [];

  return (
    <div data-testid="my-benefits-page" className="p-4 sm:p-6 space-y-5 max-w-5xl mx-auto">
      <div className="flex items-center gap-2">
        <HeartHandshake className="w-6 h-6 text-[#D4AF37]" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">My Benefits</h1>
          <p className="text-sm text-[#A3A3A3]">Your PTO, sick time, renewal and leave history</p>
        </div>
      </div>

      {!pto.is_eligible_now && (
        <div data-testid="benefits-not-eligible" className="rounded-xl border border-yellow-700/50 bg-yellow-900/10 p-4">
          <p className="text-yellow-300 font-medium text-sm">⏳ PTO becomes available on {fmtDate(pto.eligibility_date)}</p>
          <p className="text-[#A3A3A3] text-xs mt-1">
            You'll receive {pto.annual_allowance > 0 ? `${pto.annual_allowance}h of PTO per year` : 'your PTO allowance'} once eligible. Sick and unpaid leave are always available.
          </p>
        </div>
      )}

      {/* Compensation (self-only) */}
      {comp && (
        <Card title="Compensation" icon={DollarSign} testid="compensation-card">
          <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">Current Pay Rate</p>
              <p data-testid="my-current-rate" className="text-2xl font-bold text-[#D4AF37]">${(Number(comp.current_rate) || 0).toFixed(2)}<span className="text-[#A3A3A3] text-base font-normal">/hr</span></p>
              {comp.current_effective_date && <p className="text-[#737373] text-xs">Effective {fmtDate(comp.current_effective_date)}</p>}
            </div>
          </div>
          {(comp.history || []).length > 0 && (
            <div data-testid="my-pay-rate-history">
              <p className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider mb-2">Pay Rate History</p>
              <div className="space-y-1.5">
                {comp.history.map((hst) => (
                  <div key={hst.id} className="flex items-center justify-between text-sm border-b border-[#1F1F1F] py-2 last:border-0">
                    <div className="min-w-0">
                      <span className="text-white font-semibold">${(Number(hst.rate) || 0).toFixed(2)}/hr</span>
                      {hst.is_current && <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 uppercase">Current</span>}
                      {hst.is_future && <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 uppercase">Scheduled</span>}
                      <p className="text-[#A3A3A3] text-xs">
                        {hst.effective_date ? `Effective ${fmtDate(hst.effective_date)}` : 'Opening rate'}
                        {hst.through_date ? ` – ${fmtDate(hst.through_date)}` : (hst.is_current ? ' – Current' : '')}
                        {hst.reason ? ` · ${hst.reason}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* PTO + Renewal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card title="PTO Summary" icon={CalendarClock} testid="pto-summary-card">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Stat label="Current Available" value={h(pto.current_available)} accent="text-[#D4AF37]" />
              <Stat label="Pending Requests" value={h(pto.pending_requests)} />
              <Stat label="After Pending" value={h(pto.available_after_pending)} />
              <Stat label="Used YTD" value={h(pto.used_ytd)} />
              <Stat label="Annual Allowance" value={h(pto.annual_allowance)} />
              <Stat label="Eligibility Date" value={pto.eligibility_date ? fmtDate(pto.eligibility_date) : 'Eligible'} />
            </div>
          </Card>
        </div>
        <Card title="Renewal Forecast" icon={Gift} testid="renewal-forecast-card">
          <div className="space-y-3">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">Next renewal</p>
              <p className="text-lg font-bold text-white" data-testid="next-renewal-date">{pto.next_renewal_date ? fmtDate(pto.next_renewal_date) : '—'}</p>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#A3A3A3]">Annual grant</span>
              <span className="text-green-400 font-semibold">+{h(pto.renewal_grant)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#A3A3A3]">Carryover</span>
              <span className="text-white text-right" data-testid="carryover-rule">{pto.carryover_rule}</span>
            </div>
            {!pto.auto_renew && <p className="text-[11px] text-[#525252]">Auto-renew is off — your office handles renewals manually.</p>}
          </div>
        </Card>
      </div>

      {/* Sick + Holiday */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card title="Sick Time" icon={Stethoscope} testid="sick-card">
          <div className="flex items-center justify-between">
            <Stat label="Sick Time" value="Unlimited" accent="text-green-400" />
            <Stat label="Used YTD" value={h(sick.used_ytd)} />
          </div>
          <p className="text-xs text-[#A3A3A3] mt-3">Policy: <span className="text-white font-medium capitalize" data-testid="sick-policy">{sick.policy}</span> {sick.policy === 'paid' ? '(sick time is paid)' : '(sick time is unpaid but always available)'}</p>
        </Card>
        <Card title="Paid Holidays" icon={CheckCircle2} testid="holiday-card">
          {!paid_holiday_eligible ? (
            <p className="text-[#A3A3A3] text-sm" data-testid="holiday-status">You are not currently eligible for paid company holidays.</p>
          ) : (
            <div className="space-y-3">
              <div data-testid="holiday-upcoming">
                <p className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider mb-2">Upcoming Paid Holidays</p>
                {holidays.upcoming.length === 0 ? (
                  <p className="text-xs text-[#525252]">No upcoming paid holidays scheduled.</p>
                ) : (
                  <div className="space-y-1.5">
                    {holidays.upcoming.map((hol, i) => (
                      <div key={i} data-testid={`holiday-up-${i}`} className="flex items-center justify-between rounded-lg border border-[#262626] bg-[#0F0F0F] px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate">{hol.name}</p>
                          <p className="text-[#A3A3A3] text-xs">{fmtDate(hol.date)}</p>
                        </div>
                        <span className="text-green-400 text-sm font-semibold shrink-0">{h(hol.paid_hours)} paid</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {holidays.past.length > 0 && (
                <div data-testid="holiday-past">
                  <p className="text-xs font-semibold text-[#525252] uppercase tracking-wider mb-2 mt-3">Past Paid Holidays (this year)</p>
                  <div className="space-y-1">
                    {holidays.past.map((hol, i) => (
                      <div key={i} className="flex items-center justify-between text-xs text-[#A3A3A3] py-1">
                        <span>{fmtDate(hol.date)} — {hol.name}</span>
                        <span>{h(hol.paid_hours)} paid</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Benefit Resources */}
      {benefitLinks.length > 0 && (
        <Card title="Benefit Resources" icon={ExternalLink} testid="benefit-links-card">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {benefitLinks.map((link) => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`benefit-link-${link.category}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-[#262626] bg-[#0F0F0F] hover:border-[#D4AF37]/50 hover:bg-[#141414] transition-colors px-4 py-3.5 group"
              >
                <div className="min-w-0">
                  <p className="text-white font-medium text-sm truncate">{link.display_name}</p>
                  {link.description && <p className="text-[#A3A3A3] text-xs truncate">{link.description}</p>}
                </div>
                <ExternalLink className="w-4 h-4 text-[#D4AF37] shrink-0 group-hover:scale-110 transition-transform" />
              </a>
            ))}
          </div>
        </Card>
      )}

      {/* Time-Off History */}
      <Card title="Time-Off History" icon={History} testid="history-card">
        <Section title="Pending Requests" items={history.pending} empty="No pending requests." testid="history-pending" />
        <Section title="Upcoming Approved Time Off" items={history.upcoming} empty="No upcoming approved time off." testid="history-upcoming" />
        <Section title="Past Time Off" items={history.past} empty="No past time off yet." testid="history-past" />
      </Card>

      {/* PTO Activity (ledger) */}
      <Card title="PTO Activity" icon={Clock} testid="ledger-card">
        {ledger.length === 0 ? (
          <p className="text-xs text-[#525252]">No PTO activity yet.</p>
        ) : (
          <div className="space-y-1.5">
            {ledger.map((t, i) => (
              <div key={i} data-testid={`ledger-item-${i}`} className="flex items-center justify-between text-sm border-b border-[#1F1F1F] py-2 last:border-0">
                <div className="min-w-0">
                  <span className="text-white">{t.label}</span>
                  <span className="text-[#525252] text-xs ml-2">{fmtDate(t.date)}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={t.hours < 0 ? 'text-red-400' : 'text-green-400'}>{t.hours > 0 ? '+' : ''}{h(t.hours)}</span>
                  <span className="text-[#A3A3A3] w-16 text-right">{h(t.balance_after)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      {/* Bonus History (self-only) */}
      <Card title="Bonus History" icon={Award} testid="bonus-history-card">
        {bonuses.length === 0 ? (
          <p className="text-xs text-[#525252]">No bonuses yet.</p>
        ) : (
          <div className="space-y-1.5">
            {bonuses.map((b) => {
              const amt = (b.final_amount != null ? b.final_amount : b.proposed_amount) || 0;
              const st = (b.status || 'pending').toLowerCase();
              const stStyle = st === 'paid' ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : (STATUS_STYLE[st] || 'bg-[#333] text-[#A3A3A3]');
              return (
                <div key={b.id} data-testid={`bonus-row-${b.id}`} className="flex items-start justify-between gap-3 text-sm border-b border-[#1F1F1F] py-2 last:border-0">
                  <div className="min-w-0">
                    <span className="text-white font-medium capitalize">{String(b.bonus_type || 'bonus').replace(/_/g, ' ')}</span>
                    <span className={`ml-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded ${stStyle}`}>{st}</span>
                    <p className="text-[#A3A3A3] text-xs">
                      {fmtDate(b.service_date || (b.created_at ? String(b.created_at).slice(0, 10) : null))}
                      {b.customer_name ? ` · ${b.customer_name}` : ''}
                      {b.work_order_number ? ` · WO ${b.work_order_number}` : ''}
                    </p>
                  </div>
                  <span className="text-[#D4AF37] font-bold shrink-0">${(Number(amt) || 0).toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default MyBenefits;
